import React, { useState, useEffect, useRef } from "react";
import io from 'socket.io-client';
import { getToken } from "../../utils/token";
import { postStartMigrationInterEtoile, getCurrentSearchUsers } from "../../api/internal/usersInternalApi";
import {
    SearchField, SearchInput, SearchLoading, Dropdown, DropdownItem,
    UserInfo, UserName, UserEmail, UserDepartment, NoResults,
    TagsContainer, UserTag, TagRemove, SelectedCount,
    MigrationUserCard, CardHeader, CardUserName, CardUserEmail,
    CardLogs, LogEntry, LogStep, LogStatus,
} from "./migration.styled";
import {
    Card, CardTitle, PageHead, PageActions, Button, Pill, PillDot, Select,
    FormRow, FieldLabel, Notice,
} from "../ui";
import { AVAILABLE_STARS } from "../../utils/getCompanyName";

// Correspondance statut → ton de pill du système
const STATUS_PILL = {
    complete: { tone: 'ok', label: 'Terminé' },
    error: { tone: 'danger', label: 'Échec' },
    'in-progress': { tone: 'accent', label: 'En cours' },
    pending: { tone: 'neutral', label: 'En attente' },
};

const LOG_ICON = {
    error: 'bx bx-x-circle',
    warning: 'bx bx-error',
    success: 'bx bx-check-circle',
    pending: 'bx bx-loader-alt bx-spin',
};

export default function Migration() {
    // Socket
    const [socketId, setSocketId] = useState(null);
    const socketRef = useRef();

    // Sélection
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [targetStar, setTargetStar] = useState('');

    // Recherche utilisateurs
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef(null);

    // Migration
    const [isRunning, setIsRunning] = useState(false);
    const [migrationLogs, setMigrationLogs] = useState({});
    const [formError, setFormError] = useState('');

    // ─── Socket Connection ───────────────────────────────────────
    useEffect(() => {
        const initSocket = async () => {
            const token = await getToken();

            socketRef.current = io(process.env.REACT_APP_API_URL || 'https://habilitations.constellation.fr', {
                auth: { token },
                path: '/socket.io',
                transports: ['websocket', 'polling'],
            });

            socketRef.current.on('connect', () => {
                console.log("Socket connecté:", socketRef.current.id);
                setSocketId(socketRef.current.id);
            });

            socketRef.current.on('migration_update', (data) => {
                setMigrationLogs((prev) => {
                    const userId = data.userId || 'global';
                    const userLogs = prev[userId] || [];
                    return {
                        ...prev,
                        [userId]: [...userLogs, data]
                    };
                });

                if (data.finished && data.error) {
                    console.error(`Migration échouée pour ${data.userId}`);
                }

                if (data.finished && data.allCompleted) {
                    setIsRunning(false);
                }
            });

            socketRef.current.on('connect_error', (err) => {
                console.error("Erreur connexion socket:", err.message);
            });
        };

        initSocket();

        return () => {
            if (socketRef.current) socketRef.current.disconnect();
        };
    }, []);

    // ─── Recherche utilisateurs (debounce) ───────────────────────
    useEffect(() => {
        const delaySearch = setTimeout(async () => {
            if (searchQuery.length >= 2) {
                setIsSearching(true);
                try {
                    const results = await getCurrentSearchUsers(searchQuery);
                    const filtered = results.filter(
                        (user) => !selectedUsers.some((u) => u.objectId === user.objectId)
                    );
                    setSearchResults(filtered);
                    setShowDropdown(true);
                } catch (error) {
                    console.error("Erreur recherche:", error);
                    setSearchResults([]);
                }
                setIsSearching(false);
            } else {
                setSearchResults([]);
                setShowDropdown(false);
            }
        }, 300);

        return () => clearTimeout(delaySearch);
    }, [searchQuery, selectedUsers]);

    // ─── Fermer dropdown si clic extérieur ───────────────────────
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ─── Handlers ────────────────────────────────────────────────
    const handleSelectUser = (user) => {
        setSelectedUsers((prev) => [...prev, user]);
        setSearchQuery('');
        setSearchResults([]);
        setShowDropdown(false);
    };

    const handleRemoveUser = (userId) => {
        setSelectedUsers((prev) => prev.filter((u) => u.objectId !== userId));
    };

    const handleStartMigration = async () => {
        setFormError('');

        if (!socketId) {
            setFormError("Connexion au serveur non établie, veuillez patienter.");
            return;
        }

        if (selectedUsers.length === 0) {
            setFormError("Sélectionnez au moins un collaborateur.");
            return;
        }

        if (!targetStar) {
            setFormError("Sélectionnez une étoile cible.");
            return;
        }

        const usersOnSameStar = selectedUsers.filter((user) => {
            const userStar = AVAILABLE_STARS.find((s) =>
                user.userPrincipalName?.toLowerCase().includes(s.value.toLowerCase())
            );
            return userStar?.value === targetStar;
        });

        if (usersOnSameStar.length > 0) {
            setFormError(
                `Déjà sur l'étoile ${targetStar} : ${usersOnSameStar.map(u => u.displayName).join(', ')}`
            );
            return;
        }

        setIsRunning(true);
        setMigrationLogs({});

        await postStartMigrationInterEtoile(
            selectedUsers.map((u) => u.userPrincipalName),
            socketId,
            targetStar,
            AVAILABLE_STARS.find((s) => targetStar === s.value)?.domaine,
        );
    };

    const getLogVariant = (log) => {
        if (log.error) return 'error';
        if (log.warning) return 'warning';
        // 'Terminé' était testé par l'ancien getStepIcon : sans lui, une étape
        // achevée dont le status ne porte pas ✅ retomberait en « en attente ».
        if (log.finished || log.status?.includes('✅') || log.status?.includes('Terminé')) return 'success';
        return 'pending';
    };

    const getCardStatus = (userLogs) => {
        const lastLog = userLogs[userLogs.length - 1];
        if (!lastLog) return 'pending';
        if (lastLog.finished && lastLog.error) return 'error';
        if (lastLog.finished) return 'complete';
        return 'in-progress';
    };

    return (
        <>
            <PageHead>
                <div>
                    <h2>Migration</h2>
                    <p>Migrer un ou plusieurs collaborateurs vers une nouvelle étoile.</p>
                </div>
                <PageActions>
                    <Pill $tone={socketId ? 'ok' : 'danger'} title={socketId || 'Aucune connexion'}>
                        <PillDot />
                        {socketId ? 'Serveur connecté' : 'Serveur déconnecté'}
                    </Pill>
                </PageActions>
            </PageHead>

            <Card>
                <CardTitle>Configuration</CardTitle>

                <FormRow>
                    <FieldLabel>
                        Collaborateurs à migrer
                        <small>Recherchez par nom ou adresse mail</small>
                    </FieldLabel>
                    <SearchField ref={searchRef}>
                        <SearchInput
                            type="text"
                            placeholder="Rechercher un collaborateur..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            disabled={isRunning}
                        />

                        {isSearching && <SearchLoading>Recherche...</SearchLoading>}

                        {showDropdown && searchResults.length > 0 && (
                            <Dropdown>
                                {searchResults.map((user) => (
                                    <DropdownItem
                                        key={user.objectId}
                                        onClick={() => handleSelectUser(user)}
                                    >
                                        <UserInfo>
                                            <UserName>{user.displayName}</UserName>
                                            <UserEmail>{user.userPrincipalName}</UserEmail>
                                        </UserInfo>
                                        <UserDepartment>{user.department || '—'}</UserDepartment>
                                    </DropdownItem>
                                ))}
                            </Dropdown>
                        )}

                        {showDropdown && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                            <NoResults>Aucun collaborateur trouvé</NoResults>
                        )}
                    </SearchField>

                    {selectedUsers.length > 0 && (
                        <TagsContainer>
                            {selectedUsers.map((user) => (
                                <UserTag key={user.objectId}>
                                    {user.displayName}
                                    <TagRemove
                                        type="button"
                                        onClick={() => handleRemoveUser(user.objectId)}
                                        disabled={isRunning}
                                        title="Retirer"
                                    >
                                        ×
                                    </TagRemove>
                                </UserTag>
                            ))}
                        </TagsContainer>
                    )}

                    <SelectedCount>
                        {selectedUsers.length} collaborateur{selectedUsers.length > 1 ? 's' : ''} sélectionné{selectedUsers.length > 1 ? 's' : ''}
                    </SelectedCount>
                </FormRow>

                <FormRow>
                    <FieldLabel>
                        Étoile cible
                        <small>Entité de destination</small>
                    </FieldLabel>
                    <Select
                        value={targetStar}
                        onChange={(e) => setTargetStar(e.target.value)}
                        disabled={isRunning}
                        style={{ maxWidth: '520px' }}
                    >
                        <option value="">— Sélectionner une étoile —</option>
                        {AVAILABLE_STARS.map((star) => (
                            <option key={star.value} value={star.value}>
                                {star.label} ({star.trigramme})
                            </option>
                        ))}
                    </Select>
                </FormRow>

                <Button
                    type="button"
                    onClick={handleStartMigration}
                    disabled={!socketId || isRunning || selectedUsers.length === 0 || !targetStar}
                >
                    <i className={isRunning ? 'bx bx-loader-alt bx-spin' : 'bx bx-rocket'} />
                    {isRunning ? 'Migration en cours...' : 'Lancer la migration'}
                </Button>

                {formError && (
                    <Notice $tone="warn" style={{ marginTop: '16px' }}>
                        <i className="bx bx-error" />
                        {formError}
                    </Notice>
                )}
            </Card>

            {Object.keys(migrationLogs).length > 0 && (
                <Card>
                    <CardTitle>Avancement</CardTitle>

                    {selectedUsers.map((user) => {
                        const userLogs = migrationLogs[user.userPrincipalName] || [];
                        const cardStatus = getCardStatus(userLogs);
                        const pill = STATUS_PILL[cardStatus];

                        return (
                            <MigrationUserCard key={user.objectId} $status={cardStatus}>
                                <CardHeader>
                                    <div>
                                        <CardUserName>{user.displayName}</CardUserName>
                                        <CardUserEmail>{user.userPrincipalName}</CardUserEmail>
                                    </div>
                                    <Pill $tone={pill.tone}>
                                        <PillDot />
                                        {pill.label}
                                    </Pill>
                                </CardHeader>

                                {userLogs.length > 0 && (
                                    <CardLogs>
                                        {userLogs.map((log, index) => {
                                            const variant = getLogVariant(log);
                                            return (
                                                <LogEntry key={index} $variant={variant}>
                                                    <i className={LOG_ICON[variant]} />
                                                    <LogStep>{log.step}</LogStep>
                                                    <LogStatus $variant={variant}>{log.status}</LogStatus>
                                                </LogEntry>
                                            );
                                        })}
                                    </CardLogs>
                                )}
                            </MigrationUserCard>
                        );
                    })}
                </Card>
            )}
        </>
    );
}
